import axios, {
  AxiosError,
  type InternalAxiosRequestConfig,
} from 'axios';
import { isAccessTokenStale } from './jwt-access-expiry';

/**
 * Single axios instance for the NestJS API, ported from cuentas-claras.
 *
 * Token access is injected rather than imported so this module has no
 * dependency on the auth store (which would be a cycle: the store calls the
 * client to log in). gatsby-browser.tsx wires the three setters once.
 *
 * GATSBY_API_URL is inlined at build time by Gatsby's DefinePlugin, so
 * changing it requires a rebuild. It must not throw when unset — every
 * `gatsby build` without env vars would fail, which is exactly how the old
 * src/lib/supabase.ts used to break builds.
 */
const BASE_URL = process.env.GATSBY_API_URL ?? 'http://localhost:3000';

/** Refresh this far ahead of `exp`, and whenever already expired. */
const PROACTIVE_REFRESH_LEEWAY_MS = 120_000;

const httpClient = axios.create({
  baseURL: `${BASE_URL}/api`,
  timeout: 20000,
  headers: { 'Content-Type': 'application/json' },
});

let getToken: () => string | null = () => null;
let onUnauthorized: () => void = () => {};
let refreshSession: () => Promise<boolean> = async () => false;

export const setTokenGetter = (fn: () => string | null): void => {
  getToken = fn;
};
export const setUnauthorizedHandler = (fn: () => void): void => {
  onUnauthorized = fn;
};
export const setRefreshSession = (fn: () => Promise<boolean>): void => {
  refreshSession = fn;
};

/**
 * De-dupes concurrent refreshes. Without this, a page that fires five
 * requests at once on a stale token would rotate the refresh token five
 * times; four of those rotations would race and lose, logging the user out.
 */
let refreshInFlight: Promise<boolean> | null = null;
function refreshOnce(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = refreshSession().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

/** Did this request actually carry credentials? A 401 from a public endpoint
 *  must not sign the user out. */
function requestSentAuthorization(
  config: InternalAxiosRequestConfig | undefined,
): boolean {
  if (!config?.headers) return false;
  const headers = config.headers as unknown as {
    get?: (name: string) => unknown;
    Authorization?: unknown;
    authorization?: unknown;
  };
  const value =
    typeof headers.get === 'function'
      ? (headers.get('Authorization') ?? headers.get('authorization'))
      : (headers.Authorization ?? headers.authorization);
  return typeof value === 'string' && value.trim().length > 0;
}

type RequestWithRetry = InternalAxiosRequestConfig & { _retry?: boolean };

httpClient.interceptors.request.use(async (config) => {
  // Opt-out for login and refresh themselves, which would otherwise recurse.
  if (config.headers['No-Auth']) {
    delete config.headers['No-Auth'];
    return config;
  }

  let token = getToken();
  if (token && isAccessTokenStale(token, PROACTIVE_REFRESH_LEEWAY_MS)) {
    const refreshed = await refreshOnce();
    if (!refreshed) {
      onUnauthorized();
      return Promise.reject(
        new AxiosError('Session refresh failed', 'ERR_REFRESH_FAILED', config),
      );
    }
    token = getToken();
  }

  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

httpClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const config = error.config as RequestWithRetry | undefined;

    if (
      error.response?.status !== 401 ||
      !requestSentAuthorization(config)
    ) {
      return Promise.reject(error);
    }

    // Already retried once — the refresh did not help, so stop.
    if (config?._retry) {
      onUnauthorized();
      return Promise.reject(error);
    }
    if (config) config._retry = true;

    const refreshed = await refreshOnce();
    if (!refreshed || !config) {
      onUnauthorized();
      return Promise.reject(error);
    }

    const token = getToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return httpClient(config);
  },
);

export default httpClient;
